import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { clearOTP, getOTP, getSignupDraft, setOTP } from "@/lib/storage";
import { sendOtp, verifyOtp } from "@/lib/termii";
import {
    AuthScreen,
    AuthBody,
    AuthFooter,
    ScreenHeader,
    PrimaryButton,
} from "@/screens/auth/_ui";

const OTP_LENGTH = 6;
const RESEND_SECS = 30;

/**
 * Shared OTP verification screen. Works for signup and forgot-password
 * depending on what's stored in localStorage under `neara.otp`.
 */
const OTPScreen = ({ purpose, nextRoute, testIdPrefix = "otp" }) => {
    const navigate = useNavigate();
    const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(""));
    const [error, setError] = useState(null);
    const [otp, setOtp] = useState(() => getOTP());
    const [cooldown, setCooldown] = useState(RESEND_SECS);
    const inputsRef = useRef([]);

    useEffect(() => {
        const current = getOTP();
        if (!current || current.purpose !== purpose) {
            // Guard: OTP must exist for this purpose
            navigate(-1);
            return;
        }
        setOtp(current);
    }, [purpose, navigate]);

    useEffect(() => {
        if (cooldown <= 0) return;
        const t = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
        return () => clearInterval(t);
    }, [cooldown]);

    // For signup, we need the draft for the "sent to" label
    const draft = getSignupDraft();
    const sentTo =
        otp?.identifier ||
        draft?.phone ||
        draft?.email ||
        "your device";

    const handleChange = (idx, val) => {
        const clean = val.replace(/\D/g, "").slice(0, 1);
        const next = [...digits];
        next[idx] = clean;
        setDigits(next);
        if (error) setError(null);
        if (clean && idx < OTP_LENGTH - 1) {
            inputsRef.current[idx + 1]?.focus();
        }
    };

    const handlePaste = (e) => {
        const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
        if (!pasted) return;
        e.preventDefault();
        const next = Array(OTP_LENGTH).fill("");
        for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
        setDigits(next);
        inputsRef.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
    };

    const handleKeyDown = (idx, e) => {
        if (e.key === "Backspace" && !digits[idx] && idx > 0) {
            inputsRef.current[idx - 1]?.focus();
        }
        if (e.key === "Enter") handleVerify();
    };

    const handleVerify = async () => {
        const entered = digits.join("");
        if (entered.length < OTP_LENGTH) {
            setError("Please enter the full 6-digit code");
            return;
        }
        const current = getOTP();
        if (!current || current.purpose !== purpose || !current.pinId) {
            setError("Code expired. Please request a new one.");
            return;
        }
        // Always verify through the backend — no local code comparison.
        const res = await verifyOtp({ pinId: current.pinId, code: entered });
        if (!res.ok) {
            setError(res.error || "Incorrect code. Please try again.");
            return;
        }
        // For forgot-password flow, stash the identifier so the Reset screen
        // knows which account to update after the OTP record is cleared.
        if (purpose === "forgot" && current.identifier) {
            localStorage.setItem(
                "neara.resetIdentifier",
                JSON.stringify(current.identifier)
            );
        }
        clearOTP();
        navigate(nextRoute);
    };

    const handleResend = async () => {
        if (cooldown > 0) return;
        const current = getOTP();
        const identifier = current?.identifier;
        if (!identifier) {
            setError("Couldn't find the original number/email. Please start over.");
            return;
        }
        const channel = /[a-zA-Z]/.test(identifier) ? "email" : "sms";
        const res = await sendOtp({ identifier, purpose, channel });
        if (!res.ok) {
            setError(res.error || "Could not resend code");
            return;
        }
        const next = {
            ...(current || {}),
            pinId: res.pinId || null,
            purpose,
            sentAt: Date.now(),
        };
        setOTP(next);
        setOtp(next);
        setDigits(Array(OTP_LENGTH).fill(""));
        setError(null);
        setCooldown(RESEND_SECS);
        inputsRef.current[0]?.focus();
    };

    return (
        <AuthScreen testId={`${testIdPrefix}-screen`}>
            <ScreenHeader title="Verify code" />
            <AuthBody>
                <div className="pt-4 space-y-1 animate-fade-up">
                    <h2 className="font-display text-[22px] font-semibold tracking-tight">
                        Enter the 6-digit code
                    </h2>
                    <p className="text-[13.5px] text-muted-foreground">
                        Sent to <span className="text-foreground font-medium">{sentTo}</span>
                    </p>
                </div>

                <div
                    className="mt-8 flex items-center justify-between gap-2 animate-fade-up"
                    style={{ animationDelay: "80ms" }}
                    onPaste={handlePaste}
                >
                    {digits.map((d, i) => (
                        <input
                            key={i}
                            ref={(el) => (inputsRef.current[i] = el)}
                            data-testid={`${testIdPrefix}-digit-${i}`}
                            value={d}
                            onChange={(e) => handleChange(i, e.target.value)}
                            onKeyDown={(e) => handleKeyDown(i, e)}
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            maxLength={1}
                            autoFocus={i === 0}
                            className={[
                                "w-12 h-14 text-center text-[20px] font-semibold rounded-2xl border transition-colors",
                                error
                                    ? "border-destructive bg-destructive/5"
                                    : "border-border bg-secondary focus:border-primary focus:bg-background outline-none",
                            ].join(" ")}
                        />
                    ))}
                </div>

                {error && (
                    <p
                        data-testid={`${testIdPrefix}-error`}
                        className="mt-3 text-[13px] text-destructive font-medium"
                    >
                        {error}
                    </p>
                )}

                <div className="mt-6 flex items-center justify-center gap-1.5 text-[13px]">
                    <span className="text-muted-foreground">Didn't get it?</span>
                    <button
                        data-testid={`${testIdPrefix}-resend`}
                        onClick={handleResend}
                        disabled={cooldown > 0}
                        className="font-medium text-primary disabled:text-muted-foreground disabled:cursor-not-allowed"
                    >
                        {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
                    </button>
                </div>
            </AuthBody>

            <AuthFooter>
                <PrimaryButton testId={`${testIdPrefix}-verify-btn`} onClick={handleVerify}>
                    Verify
                </PrimaryButton>
            </AuthFooter>
        </AuthScreen>
    );
};

export default OTPScreen;
